import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../../shared/helpers/icons';
import { FormsModule } from '@angular/forms';
import { EAssessmentSubTypes, EAssessmentTypes } from '../../../../shared/models/eop_types.enum';
import { UserDataService } from '../../../../shared/services/userData.service';
import { AssessmentService } from '../../../../shared/services/assessment.service';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { IContact, IUserInfo } from '../../../../shared/models/contact';
import { ContactsService } from '../../../../shared/services/contact.service';
import { NoEmojiDirective } from '../../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'send-assessment',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    FormsModule,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './send-assessment.component.html',
  styleUrl: './send-assessment.component.css'
})

export class SendAssessmentComponent implements OnInit {

  @Input() assessType: string | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  icons = Icons;
  assessmentEnum = EAssessmentTypes;
  contactsList: IContact[] = [];
  fullContactsList: IContact[] = [];
  SUBTYPES_BY_TYPE: Record<EAssessmentTypes, EAssessmentSubTypes[]> = {
    [EAssessmentTypes.Site]: [
      EAssessmentSubTypes["School Grounds"],
      EAssessmentSubTypes["Classrooms"],
      EAssessmentSubTypes["Evacuation Routes"],
      EAssessmentSubTypes["Neighborhoods and Communities"],
    ],
    [EAssessmentTypes.Capacity]: [
      EAssessmentSubTypes["Skills Survey"],
    ],
    [EAssessmentTypes.Climate]: [
      EAssessmentSubTypes["Instructional Staff"],
      EAssessmentSubTypes["Non-Instructional Staff"],
      EAssessmentSubTypes["Parent"],
      EAssessmentSubTypes["Student"],
    ],
  };

  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  newEmail: string = '';
  emails: { email: string; contact_id?: number }[] = [];
  selectedSubTypeId: number | null = null;
  currentSubTypeIds: number[] = [];

  loading: boolean = false;
  showContactsList: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private assessmentService: AssessmentService,
    private contactsService: ContactsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadSubtypesForAssessType();
    this.loadContacts();
  }

  private loadSubtypesForAssessType(): void {
    if (!this.assessType) {
      this.currentSubTypeIds = [];
      this.selectedSubTypeId = null;
      return;
    }
    const typeId = EAssessmentTypes[this.assessType as keyof typeof EAssessmentTypes];
    this.currentSubTypeIds = this.SUBTYPES_BY_TYPE[typeId] ?? [];
    this.selectedSubTypeId = this.currentSubTypeIds.length > 0 ? this.currentSubTypeIds[0] : null;
  }
  
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        // Check if user is an nside.io user
        if (this.currentUser?.email?.toLowerCase().endsWith('@nside.io')) {
          // Show full list
          this.contactsList = [...this.fullContactsList];
        } else {
          // Show only non-nside.io contacts
          this.contactsList = this.fullContactsList.filter(
            (c: any) => !c.email?.toLowerCase().endsWith('@nside.io')
          );
        }
        console.log('Contacts loaded successfully:', response);
        // Sort the list after retrieving contacts list.
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };  

  // helper to show readable label from enum value
  subTypeLabel(id: number): string {
    return (EAssessmentSubTypes as any)[id] as string;
  }

  // add email from input field
  get canAddEmail(): boolean {
    const trimmed = (this.newEmail || '').trim();
    return trimmed !== '' && !this.emails.some(e => e.email === trimmed);
  }

  addEmail(): void {
    const trimmed = this.newEmail.trim();
    if (trimmed && !this.emails.find(e => e.email === trimmed)) {
      this.emails.push({ email: trimmed });
      this.newEmail = '';
    }
  }

  isEmailSelected(email: string): boolean {
    return this.emails.some(e => e.email === email);
  }  

  // add from contacts
  addContact(contact: IContact): void {
    const trimmed = contact.email.trim();
    if (trimmed && !this.emails.find(e => e.email === trimmed)) {
      this.emails.push({ email: trimmed, contact_id: contact.id });
    }
  }

  removeEmail(index: number): void {
    this.emails.splice(index, 1);
  }

  closeModal(): void {
    this.onClose.emit();
  }

  sendAssessment(): void {
    if (this.currentBuildingId && this.assessType && this.emails.length > 0 && this.selectedSubTypeId !== null) {
      this.loading = true;
      console.log('Sending to:', this.emails);
      this.assessmentService.sendAssessmentRequest(this.currentBuildingId, String(EAssessmentTypes[this.assessType as keyof typeof EAssessmentTypes]), String(this.selectedSubTypeId), this.emails).subscribe({
        next: (response) => {
          if (response.has_error) {
            this.loading = false;
            this.onSave.emit({ type: 'unsuccess', message: response.message });
          } else {;
            this.onSave.emit({ type: 'success', message: `This occurred after sending ${this.assessType} Assessment.` });
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error.message);
          this.onSave.emit({type: 'unsuccess', 'message': error.message});
        }
      });
    }
  }

}
